import fixtureJson from "./airshowAssemblySemanticsDemo.fixture.json";

export type AssemblySemanticDisposition = "assembly-only-not-full-semantic";
export type GeometryExactnessDisposition = "independent-not-promoted";

export interface AirshowAssemblyCanary {
  readonly id: string;
  readonly label: string;
  readonly archiveMemberPath: string;
  readonly sourceSha256: string;
  readonly instanceEvidenceRevision: string;
  readonly workerBundleRevision: string;
  readonly assemblyReceiptRevision: string;
  readonly frameBindingRevision: string;
  readonly topologyRevision: string;
  readonly occurrenceCount: number;
  readonly fixedJointCount: number;
  readonly sourceParentEdgeCount: number;
  readonly evaluatedAttachmentCount: number;
  readonly rootCount: number;
  readonly semanticDisposition: AssemblySemanticDisposition;
  readonly geometryExactnessDisposition: GeometryExactnessDisposition;
  readonly isNegativePartialGuard: boolean;
}

export interface AssemblyDemoNode {
  readonly sourceComponentId: string;
  readonly occurrenceId: string;
  readonly parentSourceComponentId: string | null;
  readonly parentOccurrenceId: string | null;
  readonly label: string;
  readonly semanticKind: string;
  readonly depth: number;
  readonly screenX: number;
  readonly screenY: number;
}

export interface AssemblyPoseNode extends AssemblyDemoNode {
  readonly selected: boolean;
  readonly motion: "moved" | "fixed";
  readonly offsetM: number;
}

export interface AssemblyPoseSnapshot {
  readonly nodes: readonly AssemblyPoseNode[];
  readonly selectedOccurrenceId: string;
  readonly movedOccurrenceIds: readonly string[];
  readonly unchangedOccurrenceIds: readonly string[];
  readonly topologyRevision: string;
}

interface GeneratedAssemblyFixture {
  readonly revision: string;
  readonly openVspVersion: string;
  readonly canaries: readonly AirshowAssemblyCanary[];
  readonly hondaJet: {
    readonly selectedSourceComponentId: string;
    readonly selectedOccurrenceId: string;
    readonly nodes: readonly Omit<AssemblyDemoNode, "screenX" | "screenY">[];
    readonly assemblyReceipt: Record<string, unknown>;
  };
}

const REVISION = /^sha256:[0-9a-f]{64}$/;
const FIXTURE_SCHEMA = "loom.openvsp-aircraft-assembly-demo-fixture/v2";
const RECEIPT_SCHEMA = "loom.openvsp-aircraft-assembly-import/v1";
const ASSEMBLY_ONLY: AssemblySemanticDisposition = "assembly-only-not-full-semantic";
const EXACTNESS_INDEPENDENT: GeometryExactnessDisposition = "independent-not-promoted";

function record(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Record<string, unknown>;
}

function text(value: unknown, path: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`${path} must be text`);
  }
  return value;
}

function revision(value: unknown, path: string): string {
  const parsed = text(value, path);
  if (!REVISION.test(parsed)) throw new Error(`${path} must be a revision`);
  return parsed;
}

function integer(value: unknown, path: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0) {
    throw new Error(`${path} must be a non-negative integer`);
  }
  return value as number;
}

function nullableText(value: unknown, path: string): string | null {
  return value === null ? null : text(value, path);
}

function parseCanary(value: unknown, index: number): AirshowAssemblyCanary {
  const row = record(value, `canaries[${index}]`);
  const semanticDisposition = text(row.semanticDisposition, "semanticDisposition");
  const exactnessDisposition = text(
    row.geometryExactnessDisposition,
    "geometryExactnessDisposition",
  );
  if (semanticDisposition !== ASSEMBLY_ONLY || exactnessDisposition !== EXACTNESS_INDEPENDENT) {
    throw new Error(`canaries[${index}] dispositions differ`);
  }
  if (typeof row.isNegativePartialGuard !== "boolean") {
    throw new Error(`canaries[${index}].isNegativePartialGuard must be boolean`);
  }
  return {
    id: text(row.id, "id"),
    label: text(row.label, "label"),
    archiveMemberPath: text(row.archiveMemberPath, "archiveMemberPath"),
    sourceSha256: text(row.sourceSha256, "sourceSha256"),
    instanceEvidenceRevision: revision(row.instanceEvidenceRevision, "instanceEvidenceRevision"),
    workerBundleRevision: revision(row.workerBundleRevision, "workerBundleRevision"),
    assemblyReceiptRevision: revision(row.assemblyReceiptRevision, "assemblyReceiptRevision"),
    frameBindingRevision: revision(row.frameBindingRevision, "frameBindingRevision"),
    topologyRevision: revision(row.topologyRevision, "topologyRevision"),
    occurrenceCount: integer(row.occurrenceCount, "occurrenceCount"),
    fixedJointCount: integer(row.fixedJointCount, "fixedJointCount"),
    sourceParentEdgeCount: integer(row.sourceParentEdgeCount, "sourceParentEdgeCount"),
    evaluatedAttachmentCount: integer(row.evaluatedAttachmentCount, "evaluatedAttachmentCount"),
    rootCount: integer(row.rootCount, "rootCount"),
    semanticDisposition: ASSEMBLY_ONLY,
    geometryExactnessDisposition: EXACTNESS_INDEPENDENT,
    isNegativePartialGuard: row.isNegativePartialGuard,
  };
}

function parseNode(
  value: unknown,
  index: number,
): Omit<AssemblyDemoNode, "screenX" | "screenY"> {
  const row = record(value, `hondaJet.nodes[${index}]`);
  return {
    sourceComponentId: text(row.sourceComponentId, "sourceComponentId"),
    occurrenceId: text(row.occurrenceId, "occurrenceId"),
    parentSourceComponentId: nullableText(row.parentSourceComponentId, "parentSourceComponentId"),
    parentOccurrenceId: nullableText(row.parentOccurrenceId, "parentOccurrenceId"),
    label: text(row.label, "label"),
    semanticKind: text(row.semanticKind, "semanticKind"),
    depth: integer(row.depth, "depth"),
  };
}

function parseFixture(value: unknown): GeneratedAssemblyFixture {
  const root = record(value, "fixture");
  if (root.schema !== FIXTURE_SCHEMA) throw new Error("fixture schema differs");
  const openVspVersion = text(root.openVspVersion, "fixture.openVspVersion");
  if (!/^OpenVSP [0-9]+\.[0-9]+\.[0-9]+$/.test(openVspVersion)) {
    throw new Error("fixture OpenVSP version differs");
  }
  const canaryRows = root.canaries;
  if (!Array.isArray(canaryRows) || canaryRows.length !== 5) {
    throw new Error("fixture must contain five canaries");
  }
  const canaries = canaryRows.map(parseCanary);
  const honda = record(root.hondaJet, "hondaJet");
  const nodeRows = honda.nodes;
  if (!Array.isArray(nodeRows)) throw new Error("hondaJet.nodes must be an array");
  const nodes = nodeRows.map(parseNode);
  const receipt = record(honda.assemblyReceipt, "hondaJet.assemblyReceipt");
  if (
    receipt.schema !== RECEIPT_SCHEMA
    || receipt.geometryExactnessDisposition !== EXACTNESS_INDEPENDENT
    || receipt.semanticScopeDisposition !== ASSEMBLY_ONLY
  ) throw new Error("HondaJet receipt contract differs");
  const fixedJoints = receipt.fixedJoints;
  if (!Array.isArray(fixedJoints) || fixedJoints.length !== nodes.length) {
    throw new Error("HondaJet fixed-joint closure differs");
  }
  const jointBySource = new Map(fixedJoints.map((value, index) => {
    const row = record(value, `fixedJoints[${index}]`);
    if (
      row.transformEvidence !== "openvsp-post-update-model-matrix"
      || row.localTransformDerivation !== "inverse-parent-world-times-child-world/v1"
    ) throw new Error(`fixedJoints[${index}] evidence differs`);
    return [text(row.sourceComponentId, "sourceComponentId"), row] as const;
  }));
  if (jointBySource.size !== nodes.length) throw new Error("HondaJet source closure differs");
  for (const node of nodes) {
    const joint = jointBySource.get(node.sourceComponentId);
    if (
      !joint
      || joint.occurrenceId !== node.occurrenceId
      || joint.sourceParentComponentId !== node.parentSourceComponentId
      || joint.parentOccurrenceId !== node.parentOccurrenceId
    ) throw new Error(`HondaJet node ${node.sourceComponentId} mapping differs`);
  }
  const selectedSourceComponentId = text(
    honda.selectedSourceComponentId,
    "selectedSourceComponentId",
  );
  const selectedOccurrenceId = text(honda.selectedOccurrenceId, "selectedOccurrenceId");
  const selected = nodes.find((node) => node.sourceComponentId === selectedSourceComponentId);
  if (!selected || selected.occurrenceId !== selectedOccurrenceId) {
    throw new Error("HondaJet selected identity mapping differs");
  }
  const hondaCanary = canaries.find((row) => row.id === "hondaha420hondajet");
  const frameBinding = record(receipt.frameBinding, "frameBinding");
  if (
    !hondaCanary
    || hondaCanary.occurrenceCount !== nodes.length
    || hondaCanary.fixedJointCount !== fixedJoints.length
    || hondaCanary.assemblyReceiptRevision !== revision(receipt.revision, "receipt.revision")
    || hondaCanary.frameBindingRevision !== revision(frameBinding.revision, "frameBinding.revision")
    || hondaCanary.topologyRevision
    !== revision(receipt.assemblyDefinitionRevision, "assemblyDefinitionRevision")
  ) throw new Error("HondaJet canary does not bind the generated receipt");
  return {
    revision: revision(root.revision, "fixture.revision"),
    openVspVersion,
    canaries,
    hondaJet: {
      selectedSourceComponentId,
      selectedOccurrenceId,
      nodes,
      assemblyReceipt: receipt,
    },
  };
}

function layoutNodes(
  rows: readonly Omit<AssemblyDemoNode, "screenX" | "screenY">[],
): readonly AssemblyDemoNode[] {
  const children = new Map<string | null, Array<typeof rows[number]>>();
  for (const row of rows) {
    const siblings = children.get(row.parentOccurrenceId) ?? [];
    siblings.push(row);
    children.set(row.parentOccurrenceId, siblings);
  }
  const yByOccurrence = new Map<string, number>();
  let leafIndex = 0;
  const place = (row: typeof rows[number]): number => {
    const descendants = children.get(row.occurrenceId) ?? [];
    const y = descendants.length === 0
      ? 58 + leafIndex++ * 68
      : descendants.map(place).reduce((sum, item) => sum + item, 0) / descendants.length;
    yByOccurrence.set(row.occurrenceId, y);
    return y;
  };
  for (const root of children.get(null) ?? []) place(root);
  return rows.map((row) => ({
    ...row,
    screenX: 88 + row.depth * 142,
    screenY: yByOccurrence.get(row.occurrenceId) ?? 58,
  }));
}

const FIXTURE = parseFixture(fixtureJson);
const HONDA_CANARY = FIXTURE.canaries.find((row) => row.id === "hondaha420hondajet")!;
const RECEIPT = FIXTURE.hondaJet.assemblyReceipt;
const FRAME_BINDING = record(RECEIPT.frameBinding, "frameBinding");

export const AIRSHOW_ASSEMBLY_FIXTURE_REVISION = FIXTURE.revision;
export const AIRSHOW_ASSEMBLY_SOURCE_TOOL_VERSION = FIXTURE.openVspVersion;
export const AIRSHOW_ASSEMBLY_CANARIES = FIXTURE.canaries;
export const HONDAJET_ASSEMBLY_DEMO = {
  selectedSourceComponentId: FIXTURE.hondaJet.selectedSourceComponentId,
  selectedOccurrenceId: FIXTURE.hondaJet.selectedOccurrenceId,
  instanceEvidenceRevision: HONDA_CANARY.instanceEvidenceRevision,
  frameBindingRevision: revision(FRAME_BINDING.revision, "frameBinding.revision"),
  topologyRevision: HONDA_CANARY.topologyRevision,
  importReceiptRevision: HONDA_CANARY.assemblyReceiptRevision,
  nodes: layoutNodes(FIXTURE.hondaJet.nodes),
} as const;

function descendantsOf(occurrenceId: string): Set<string> {
  const selected = new Set([occurrenceId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const node of HONDAJET_ASSEMBLY_DEMO.nodes) {
      if (
        node.parentOccurrenceId
        && selected.has(node.parentOccurrenceId)
        && !selected.has(node.occurrenceId)
      ) {
        selected.add(node.occurrenceId);
        changed = true;
      }
    }
  }
  return selected;
}

export function buildAssemblyPose(offsetM: number): AssemblyPoseSnapshot {
  if (!Number.isFinite(offsetM) || Math.abs(offsetM) > 3) {
    throw new RangeError("assembly demo offset must be finite and within ±3 m");
  }
  const selectedOccurrenceId = HONDAJET_ASSEMBLY_DEMO.selectedOccurrenceId;
  const moved = offsetM === 0 ? new Set<string>() : descendantsOf(selectedOccurrenceId);
  const movedOccurrenceIds = moved.has(selectedOccurrenceId)
    ? [
      selectedOccurrenceId,
      ...[...moved].filter((id) => id !== selectedOccurrenceId).sort(),
    ]
    : [];
  const nodes = HONDAJET_ASSEMBLY_DEMO.nodes.map((node): AssemblyPoseNode => {
    const isMoved = moved.has(node.occurrenceId);
    return {
      ...node,
      screenY: node.screenY - (isMoved ? offsetM * 18 : 0),
      selected: node.occurrenceId === selectedOccurrenceId,
      motion: isMoved ? "moved" : "fixed",
      offsetM: isMoved ? offsetM : 0,
    };
  });
  return {
    nodes,
    selectedOccurrenceId,
    movedOccurrenceIds,
    unchangedOccurrenceIds: nodes
      .filter((node) => node.motion === "fixed")
      .map((node) => node.occurrenceId),
    topologyRevision: HONDAJET_ASSEMBLY_DEMO.topologyRevision,
  };
}
